import type { AlmSourceId, BoardType, IdMap } from "./baseTypes";
import type { BoardCard } from "./card";
import type { RelativeCoordinate, SizedRectangle } from "./coordinates";
import type { FlexType } from "./flexboard";
import type { Objective } from "./objective";
import type { Iteration, Team } from "./session";
import type { Shape } from "./shape";
import type { StatusClassDistribution } from "./status";

export interface ZoomedRectangle extends SizedRectangle {
  zoom: number;
}

export interface BoardId {
  boardId: string;
}

interface BaseBoardData {
  id: string;
  type: BoardType;
  almSources: AlmSource[];
  cardSize: CardSize;
  cards: IdMap<BoardCard>;
  maxZ: number;
  state: BoardState;
  artId?: string;
  userHeartbeatId?: number;
  selected: IdMap<boolean>;
  shapes: Shape[];
}

export interface BoardState {
  state: "inited" | "loading" | "loaded" | "error";
  finishedTicks: number; // number of first few vue ticks after a finished state has been reached
  loadedAt: number;
  hasStickyStatusDistribution?: boolean;
}

export const MAX_FINISHED_TICKS = 5;

export function isFinishedState(state: BoardState) {
  return state.state === "loaded" || state.state === "error";
}

export type CardSize = RelativeCoordinate & { factor: number };

interface Objectives {
  objectivesResolved: boolean;
  objectives: Objective[];
  stretchObjectives: Objective[];
}

export type BoardData<T extends BoardType> = BaseBoardData &
  {
    backlog: { type: "backlog" };
    program: { type: "program"; swimlanes: Swimlane[] } & Objectives;
    solution_backlog: { type: "solution_backlog" };
    solution: { type: "solution" };
    risk: { type: "risk" };
    team: { type: "team"; iterationStates: IterationState[]; team: Team } & Objectives;
    flex: { type: "flex"; flexType: FlexType; name: string };
    objectives: { type: "objectives" };
  }[T];

export type Board =
  | BoardData<"backlog">
  | BoardData<"program">
  | BoardData<"solution_backlog">
  | BoardData<"solution">
  | BoardData<"risk">
  | BoardData<"team">
  | BoardData<"flex">
  | BoardData<"objectives">;

export type BoardWithObjectives = BoardData<"program"> | BoardData<"team">;

export function isBacklogBoard(type?: BoardType) {
  return type === "backlog" || type === "solution_backlog";
}

export function isSolutionBacklogBoard(type?: BoardType) {
  return type === "solution_backlog";
}

export function isPlanningBoard(type?: BoardType) {
  return type === "program" || type === "solution";
}

export function isSolutionBoard(type?: BoardType) {
  return type === "solution" || type === "solution_backlog";
}

export function isArtBoard(type?: BoardType) {
  return type === "backlog" || type === "program" || type === "risk" || type === "objectives";
}

export function isTeamBoard(type?: BoardType) {
  return type === "team";
}

export function isRiskBoard(type?: BoardType) {
  return type === "risk";
}

export function isObjectivesBoard(type?: BoardType) {
  return type === "objectives";
}

export function isStickyBoard(type?: BoardType) {
  return !!type && type !== "objectives";
}

export function isFlexBoard(type?: BoardType) {
  return type === "flex";
}

export function isBoardWithObjectives(type?: BoardType) {
  return type === "program" || type === "team";
}

export function hasBoardObjectives(board: Board): board is BoardWithObjectives {
  return isBoardWithObjectives(board.type);
}

/**
 * A frontend board is a board that exists only in the frontend,
 * i.e. no database representation exists for it.
 */
export function isFrontendBoard(type?: BoardType) {
  return type === "objectives";
}

export function assureBoardType<T extends BoardType>(
  board: Board | undefined,
  type: T,
): BoardData<T> | undefined {
  return board?.type === type ? (board as BoardData<T>) : undefined;
}

export type Swimlane =
  | { type: "milestones" | "otherArts" | "empty" }
  | { type: "team"; teamIds: [string] }
  | { type: "external"; teamIds: string[]; name?: string };

export interface AlmSource {
  id: AlmSourceId;
  name: string;
}

export interface GroupIteration extends Iteration {
  load?: number;
  velocity?: number;
  statusDistribution?: StatusClassDistribution;
}

export interface BoardIteration {
  load: number;
  velocity: number;
  statusDistribution?: StatusClassDistribution;
}

export interface IterationState {
  status: IterationStatus | null;
  detail: string | null;
}

export type IterationStatus = "syncing" | "synced" | "success" | "fail";

export interface KanbanFilters {
  text: string;
  typeIds: string[];
}

export interface Kanban {
  name: string;
  columns: KanbanColumn[];
}

export interface KanbanColumn {
  name: string;
  statuses: string[];
}
